import type { User } from '@homeviews/models'

export const HAS_PERMISSION = (
  user: User,
  permission = '',
  permissionType: 'development' | 'global',
  meta: any = null,
) => {
  if (!user || !permission) return

  if (permissionType === 'development') {
    return user?.development_permissions?.[meta?.devId]?.permissions?.[
      permission
    ]
  } else {
    return user?.global_permissions?.[permission]
  }

  return false
}

// If User has at least 1 Development permission
// passed in permissions Array, return true.
export const HAS_SOME_DEVELOPMENT_PERMISSIONS = (
  user: User,
  permissions: Array<string>, // e.g. ['REPLY_TO_REVIEWS']
) => {
  if (!user?.development_permissions) return false
  return Object.keys(user.development_permissions).some((dmentId) => {
    const meta = { devId: parseInt(dmentId) }
    return permissions.some((permission) =>
      HAS_PERMISSION(user, permission, 'development', meta),
    )
  })
}

// Centralised CompanyGroupAdmin detection. The whoami response does not return
// a role for these users, so they are identified by a non-empty `developers`
// array (the companies they oversee). Keep all such checks going through this
// helper.
export const IS_COMPANY_GROUP_ADMIN = (
  user: User | null | undefined,
): boolean => {
  return !!user?.developers?.length
}

// Internal staff (SuperAdmin / Admin) always get the full navigation.
export const IS_INTERNAL_ADMIN = (user: User | null | undefined): boolean => {
  return !!user?.roles?.some(
    (role: { name?: string }) =>
      role?.name === 'SuperAdmin' || role?.name === 'Admin',
  )
}

// Whether the slimmed down navigation applies to this user (RVW-1718): the
// flag is set in Athena, and internal admins bypass it.
export const HAS_SIMPLIFIED_NAVIGATION = (
  user: User | null | undefined,
): boolean => {
  return !!user?.simplified_navigation && !IS_INTERNAL_ADMIN(user)
}

export const HAS_VIEW_DEVELOPMENT_PERMISSION = (user: User) => {
  let hasPermission = false

  if (
    user?.roles?.[0]?.name === 'SuperAdmin' ||
    user?.roles?.[0]?.name === 'Admin' ||
    user?.roles?.[0]?.name === 'Data Viewer'
  ) {
    return true
  }

  const devPermissions = user?.development_permissions || {}
  Object.keys(devPermissions).map((devId: any) => {
    const development = devPermissions[devId]

    if (development.permissions?.['VIEW_DEVELOPMENT']) {
      hasPermission = true
      return false
    }
  })

  return hasPermission
}

export const HAS_VIEW_RESOURCES_PERMISSION = (user: User) => {
  let hasPermission = false

  const devPermissions = user?.development_permissions || {}
  Object.keys(devPermissions).map((devId: any) => {
    const development = devPermissions[devId]

    if (development.permissions?.['VIEW_MARKETING_MATERIALS']) {
      hasPermission = true
      return false
    }
  })

  if (user?.global_permissions?.['VIEW_MARKETING_MATERIALS']) {
    hasPermission = true
  }

  return hasPermission
}
